import type { Address, RateMethod } from '../types';
import type { Unit } from '../Units/types';

export type { Address, RateMethod } from '../types';
export type { Unit } from '../Units/types';

export type PmsType = 'RENT_MANAGER' | 'APPFOLIO' | 'RESMAN';

export type PmsCapability = 'csv_export' | 'api_upload' | 'api_config';

export const PMS_PROVIDERS: Record<
  PmsType,
  {
    label: string;
    capabilities: PmsCapability[];
    /** When true, this provider is hidden in the structure-settings dropdown unless the
     * account has the PMS_BETA feature flag. Flip to false (or omit) when the provider
     * graduates to general availability. */
    beta?: boolean;
  }
> = {
  RENT_MANAGER: {
    label: 'Rent Manager',
    capabilities: ['csv_export', 'api_upload', 'api_config'],
  },
  APPFOLIO: {
    label: 'AppFolio',
    capabilities: ['csv_export'],
    beta: true,
  },
  RESMAN: {
    label: 'ResMan',
    capabilities: ['csv_export'],
    beta: true,
  },
};

export type RentManagerConfig = {
  property_short_name: string;
  default_charge_type?: string;
  property_id?: number;
  charge_type_id?: number;
};

export type AppfolioConfig = {
  property_name: string;
};

export type ResmanConfig = {
  property_short_name: string;
  default_transaction_category?: string;
  default_payment_type?: string;
  default_split_ledger?: string;
};

export type PmsConfig = RentManagerConfig | AppfolioConfig | ResmanConfig;

export type Structure = {
  id: string;
  ix: number;
  address: Address;
  name: string;
  units: Unit[];
  rate_method: RateMethod;
  volume_units: string;
  timezone_key: string;
  pay_online_url: string | null;
  payment_instructions: string | null;
  due_date_days: number | null; // Default days until invoice due
  pms_type: PmsType | null;
  pms_config: PmsConfig | null;
};

export type DeviceBilling = {
  device_total_volume: number;
  serial_number: string;
  name: string;
};
export type UnitBilling = {
  id: string;
  unit_cost_meter: number;
  unit_total_volume: number;
  name: string;
  devices: DeviceBilling[];
  unit_cost?: number;
  /** Cost of support-entered estimated usage (prorated to the bill period), separate
   * from unit_cost_meter. Mirrors the same field on BreakdownDialog's local Unit type. */
  unit_estimated_cost?: number;
  is_billable?: boolean;
  unit_flags?: string[];
  /** Stored unit-level setting for the daily usage bar chart on invoice PDFs (0 = HIDDEN, 1 = INCLUDED). */
  usage_graph_mode?: number;
};

export type CommonAreaUsage = {
  name: string;
  unit_id: string;
  total_volume: number;
  split_volume: number;
  split_cost: number;
};

export type BillingLineItem = {
  id: string;
  type: string;
  description: string;
  cost?: number;
  cost_units?: string;
  volume?: number;
  volume_units?: string;
  rate_method?: string;
  effective_rate?: number;
  unit_id?: string; // snake_case from generateBill API
};

export type StructureBilling = {
  billing_id: string;
  cost: number;
  cost_units: 'DOLLARS';
  effective_rate: number;
  end_time: number;
  meter_total_volume: number;
  rate_method: RateMethod;
  start_time: number;
  structure_id: string;
  volume: number;
  volume_units: 'GALLONS' | 'GALLONS_100' | 'GALLONS_1K' | 'LITERS' | 'LITERS_1K' | 'CF' | 'CCF';
  units: UnitBilling[];
  structure_name: string;
  billable_units_count?: number; // Number of billable units (excludes COMMON_AREA and NON_BILLABLE)
  common_area_usage?: CommonAreaUsage[]; // Common area usage split among billable units
  line_items?: BillingLineItem[]; // Line items from generateBill API (snake_case)
};
